import Vue from "vue";
import Vuex from "vuex";
import slugify from "slugify";
import get from "lodash/get";
import flatMap from "lodash/flatMap";
import uniqBy from "lodash/uniqBy";
import orderBy from "lodash/orderBy";
import once from "lodash/once";
import countBy from "lodash/countBy";
import intersectionBy from "lodash/intersectionBy";
import axios from "axios";

import SearchParamsDesktop from "../../components/Search/SearchParamsDesktop";
import SearchParamsMobile from "../../components/Search/SearchParamsMobile";
import Responsive from "../../components/core/Responsive";
import MobileFilters from "../../components/Search/MobileFilters/resort";
import ResortBudgetFilter from "../../components/Search/DesktopFilters/ResortBudgetFilter";
import DestinationFilter from "../../components/Search/DesktopFilters/DestinationFilter";
import GreenfinFilter from "../../components/Search/DesktopFilters/GreenfinFilter";
import RatingFilter from "../../components/Search/DesktopFilters/RatingFilter";
import FacilitiesFilter from "../../components/Search/DesktopFilters/FacilitiesFilter";
import ExperiencesFilter from "../../components/Search/DesktopFilters/ExperiencesFilter";
import ResortResultPanel from "../../components/Search/ResortResultPanel";
import ResortResultSummary from "../../components/Search/ResortResultSummary";
import ResortClearFiltersButton from "../../components/Search/ResortClearFiltersButton";
import SearchStickyNav from "../../components/Search/SearchStickyNav";
import ZbModal from "../../components/core/ZbModal";
import TagFilter from "../../components/Search/DesktopFilters/TagFilter";
import currencyRanges from "../../helpers/currencyRanges.json";

import {
  getPrimaryDestinationFilters,
  getSecondaryDestinationFilters,
  getBudgetFilters,
  getConservationFilters,
  getRatingFilters,
  applyFilterURL,
} from "../../helpers/resortSearchHelpers";
import {
  syncURLParams,
  searchWithDelay,
  getBudgetsFilter,
} from "../../helpers/searchHelpers";

const algoliasearchV4 = require("algoliasearch");

Vue.filter("handleExternalURL", (url) => {
  if (url.startsWith("http")) {
    return url;
  } else {
    return `http://${url}`;
  }
});

Vue.component("search-params-desktop", SearchParamsDesktop);
Vue.component("search-params-mobile", SearchParamsMobile);
Vue.component("mobile-filters", MobileFilters);
Vue.component("responsive", Responsive);
Vue.component("resort-budget-filter", ResortBudgetFilter);
Vue.component("destination-filter", DestinationFilter);
Vue.component("greenfin-filter", GreenfinFilter);
Vue.component("rating-filter", RatingFilter);
Vue.component("facilities-filter", FacilitiesFilter);
Vue.component("experiences-filter", ExperiencesFilter);
Vue.component("resort-clear-filters-button", ResortClearFiltersButton);
Vue.component("resort-result-panel", ResortResultPanel);
Vue.component("resort-result-summary", ResortResultSummary);
Vue.component("search-sticky-nav", SearchStickyNav);
Vue.component("zb-modal", ZbModal);
Vue.component("tag-filter", TagFilter);

Vue.use({
  install(Vue) {
    const Algolia = algoliasearchV4(
      window.zubluAlgolia.applicationID,
      window.zubluAlgolia.publicAPIKey
    );
    const resortIndex = Algolia.initIndex(window.zubluAlgolia.resortIndex);
    const destinationIndex = Algolia.initIndex(
      window.zubluAlgolia.destinationIndex
    );

    Vue.prototype.$algolia = {
      instance: Algolia,
      searchAllResorts: function({ facetFilters, numericFilters }) {
        return new Promise((resolve, reject) => {
          resortIndex
            .search("", {
              facetFilters,
              numericFilters,
              hitsPerPage: 1000,
            })
            .then(resolve)
            .catch(reject);
        });
      },
      searchDestinations: function({ facetFilters }) {
        return new Promise((resolve, reject) => {
          destinationIndex
            .search("", {
              facetFilters,
              hitsPerPage: 1000,
            })
            .then(resolve)
            .catch(reject);
        });
      },
    };
  },
});

Vue.use(Vuex);
const AVAILABLE_RANKINGS = ["top-rated", "top-pick", "price"];

const plugin = (store) => {
  window.addEventListener("onCurrencyChanged", (e) => {
    store.commit("setCurrency", e.detail.currency);
    store.dispatch("loadStatistics");
    store.dispatch("loadSuppliers");
    applyFilterURL(store.state);
  });
};

const store = new Vuex.Store({
  state() {
    const searchParams = new URLSearchParams(window.location.search);

    let page = 1;
    if (searchParams.has("page") && searchParams.get("page") !== "1") {
      page = Number(searchParams.get("page"));

      if (Number.isNaN(page)) {
        page = 1;
      }
    }

    let ranking = "top-rated";
    if (
      searchParams.has("sort") &&
      AVAILABLE_RANKINGS.includes(searchParams.get("sort"))
    ) {
      ranking = searchParams.get("sort");
    }

    if (searchParams.has("budgets")) {
      const budgets = getBudgetsFilter("resort");
      window.zubluSearchParams.budgets = budgets;
    }

    return {
      filters: window.zubluSearchParams,
      destination: window.zubluDestination,
      page,
      ranking: ranking,
      resortsResult: [],
      algoliaDestinations: [],
      statistics: null,
      searchType: window.zubluSearchType,
      from: searchParams.get("from"),
      to: searchParams.get("to"),
      guests: searchParams.get("guests"),
      isLoading: true,
      currency: window.topNavStore.state.currency,
    };
  },
  getters: {
    showExperiencesFilter(state) {
      return (
        state.destination.type === "province" ||
        state.destination.type === "country"
      );
    },
    secondaryFilteredResults(state, getters) {
      const facilities = state.filters.facilities.split(",").filter((x) => x);

      const species = state.filters.species.split(",").filter((x) => x);

      const tags = state.filters.tags.split(",").filter((x) => x);

      let filteredResorts = state.resortsResult
        .filter((resort) => {
          if (facilities.length === 0) {
            return true;
          }

          return facilities.every((item) => {
            return resort.inclusions_objects.some((obj) => obj.slug === item);
          });
        })
        .filter((resort) => {
          if (getters.experienceList.length === 0) {
            return true;
          }

          if (species.length === 0) {
            return true;
          }

          let resultDests = [];
          for (const speciesId of species) {
            let matchingDests = get(
              getters.experienceList.find(
                (exp) => exp.id.toString() === speciesId
              ),
              ["destinations"],
              []
            );

            if (resultDests.length === 0) {
              resultDests = matchingDests;
            }

            resultDests = intersectionBy(
              resultDests,
              matchingDests,
              (destination) => destination.objectID
            );
          }

          return resultDests.some((dest) => {
            return (
              resort.__platform_destination === dest.__platform_destination
            );
          });
        })
        .filter((resort) => {
          if (!resort.__platform_month_valid) {
            return true;
          }

          const startMonth = moment(
            get(
              window,
              ["topNavStore", "state", "formData", "dates", "0"],
              null
            )
          );
          if (startMonth.isValid() === false) {
            return true;
          }
          const endMonth = moment(
            get(
              window,
              ["topNavStore", "state", "formData", "dates", "1"],
              null
            )
          );
          if (endMonth.isValid() === false) {
            return true;
          }

          const monthsValid = [];
          const nStartMonth = startMonth.month() + 1;
          const nEndMonth = endMonth.month() + 1;

          if (nStartMonth < nEndMonth) {
            for (let i = nStartMonth; i <= nEndMonth; ++i) {
              monthsValid.push(i.toString());
            }
          } else {
            for (let i = nStartMonth; i <= 12; ++i) {
              monthsValid.push(i.toString());
            }

            for (let i = 1; i <= nEndMonth; ++i) {
              monthsValid.push(i.toString());
            }
          }

          const resortMonths = resort.__platform_month_valid.split(",");
          const validMonths = intersectionBy(
            resortMonths,
            monthsValid,
            (m) => m
          );

          return validMonths.length;
        })
        .filter((resort) => {
          if (tags.length === 0) {
            return true;
          }

          if (
            !resort.__platform_search_tags ||
            !resort.__platform_search_tags.length
          ) {
            return false;
          } else {
            return tags.every((tag) => {
              return resort.__platform_search_tags.includes(tag);
            });
          }
        });

      return filteredResorts;
    },
    sortedResults(state, getters) {
      // top rated by default
      let sorter = [
        ({ __odyssey_zublu_rating }) =>
          __odyssey_zublu_rating ? __odyssey_zublu_rating.zublu_rating : "",
        ({ __odyssey_zublu_rating }) =>
          __odyssey_zublu_rating ? __odyssey_zublu_rating.count : "",
        "__platform_rating",
        "__platform_tripadvisor_total_review",
        "__odyssey_price",
      ];
      let sortdir = ["desc", "desc", "desc", "desc", "asc"];

      if (state.ranking === "price") {
        sorter = [
          "__odyssey_price",
          "__platform_rating",
          "__platform_tripadvisor_total_review",
          "__odyssey_zublu_featured",
        ];
        sortdir = ["asc", "desc", "desc", "desc"];
      }
      if (state.ranking === "top-pick") {
        sorter = [
          "__odyssey_zublu_featured",
          "__platform_rating",
          "__platform_tripadvisor_total_review",
          "__odyssey_price",
        ];
        sortdir = ["desc", "desc", "desc", "desc"];
      }

      return orderBy(getters.secondaryFilteredResults, sorter, sortdir);
    },
    experienceList(state) {
      let allSpecies = flatMap(
        state.algoliaDestinations,
        (dest) => dest.__platform_species
      );

      allSpecies = allSpecies.map((s) => {
        if (!("parent" in s)) {
          return s;
        }

        return s.parent;
      });
      allSpecies = uniqBy(allSpecies, (species) => species.id);
      allSpecies = orderBy(allSpecies, ["title"], ["asc"]);

      allSpecies.forEach((species) => {
        species.destinations = state.algoliaDestinations.filter(
          (destination) => {
            return destination.__platform_species.some((_species) => {
              return (
                _species.id === species.id ||
                get(_species, ["parent", "id"], null) === species.id
              );
            });
          }
        );
      });

      return allSpecies;
    },
    availableBudgetOptions(state) {
      return get(state.statistics, ["visibleBudgets"], {});
    },
  },
  mutations: {
    setResult(state, resortsResult) {
      state.resortsResult = resortsResult;
    },
    setStatistics(state, statistics) {
      state.statistics = statistics;
    },
    setPage(state, page) {
      state.page = page;
      const searchParams = new URLSearchParams(window.location.search);

      if (state.page !== 1) {
        searchParams.set("page", state.page);
      } else {
        searchParams.delete("page");
      }

      syncURLParams(searchParams);
    },
    setRanking(state, ranking) {
      state.ranking = ranking;

      const searchParams = new URLSearchParams(window.location.search);
      if (state.ranking !== "top-rated") {
        searchParams.set("sort", state.ranking);
      } else {
        searchParams.delete("sort");
      }

      syncURLParams(searchParams);
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setAlgoliaDestinations(state, algoliaDestinations) {
      state.algoliaDestinations = algoliaDestinations;
    },
    setFilters(state, filters) {
      state.filters = filters;
      applyFilterURL(state);
    },
    setFilter(state, { name, value }) {
      state.filters[name] = value;
      applyFilterURL(state);
    },
    clearFilters(state) {
      state.filters = {
        budgets: "",
        destinations: "",
        greenfin: "",
        rating: "",
        facilities: "",
        species: "",
        tags: "",
      };
      state.page = 1;

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("budgets");
      searchParams.delete("destinations");
      searchParams.delete("greenfin");
      searchParams.delete("rating");
      searchParams.delete("facilities");
      searchParams.delete("species");
      searchParams.delete("page");
      searchParams.delete("goodFor");

      syncURLParams(searchParams);
    },
    setCurrency(state, value) {
      state.currency = value;
    },
  },
  actions: {
    loadSuppliers(context) {
      context.commit("setIsLoading", true);

      const facetFilters = [
        getPrimaryDestinationFilters(context.state.filters),
        getSecondaryDestinationFilters(context.state.filters),
        getConservationFilters(context.state.filters),
        getRatingFilters(context.state.filters),
      ].filter((f) => f);

      const numericFilters = getBudgetFilters(context.state.filters);

      return searchWithDelay(
        this._vm.$algolia.searchAllResorts,
        0
      )({ facetFilters, numericFilters })
        .then((resp) => {
          const result = resp.hits;

          result.forEach((item) => {
            item.inclusions_objects = (item.__odyssey_inclusions || [])
              .map((incl) => {
                return Object.assign(incl, {
                  slug: slugify(incl.name).toLowerCase(),
                });
              })
              .concat(
                (item.__odyssey_dive_facilities || []).map((incl) => {
                  return Object.assign(incl, {
                    slug: slugify(incl.name).toLowerCase(),
                  });
                })
              );
          });

          return result;
        })
        .then((result) => {
          const ids = result.map((item) => item.objectID);

          return window
            .fetchReviewsByIds({ supplier_ids: ids })
            .then(({ data }) => {
              if (data.length) {
                result.forEach((resort) => {
                  const rating = data.find(
                    (o) => +o.craft_id === +resort.objectID
                  );

                  if (rating) {
                    resort.rating = rating;
                  }
                });
              }
              return result;
            });
        })
        .then((result) => {
          context.commit("setResult", result);
          context.commit("setIsLoading", false);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    loadDestinations(context) {
      let facetFilters = "";

      if (window.zubluDestination.type === "country") {
        facetFilters = `__platform_country_id:${window.zubluDestination.id}`;
      } else if (window.zubluDestination.type === "province") {
        facetFilters = `__platform_region_id:${window.zubluDestination.id}`;
      }

      if (facetFilters) {
        return this._vm.$algolia
          .searchDestinations({ facetFilters })
          .then((result) => {
            context.commit("setAlgoliaDestinations", result.hits);

            return result.hits;
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    loadStatistics(context) {
      let facetFilters = [
        `__platform_destination_id:${window.zubluDestination.id}`,
        `__platform_searchable_destination_ids:${window.zubluDestination.id}`,
      ];

      if (window.zubluDestination.type === "country") {
        facetFilters = [
          `__platform_country_id:${window.zubluDestination.id}`,
          `__platform_searchable_destination_ids:${window.zubluDestination.id}`,
        ];
      } else if (window.zubluDestination.type === "province") {
        facetFilters = [
          `__platform_region_id:${window.zubluDestination.id}`,
          `__platform_searchable_destination_ids:${window.zubluDestination.id}`,
        ];
      }

      this._vm.$algolia
        .searchAllResorts({
          facetFilters: [facetFilters],
          numericFilters: "__odyssey_price > 0",
        })
        .then((results) => {
          const visibleDestinations = results.hits.reduce((agg, resort) => {
            agg[resort.__platform_destination_id] = true;
            agg[resort.__platform_region_id] = true;

            return agg;
          }, {});

          const visibleRatings = results.hits.reduce((agg, resort) => {
            agg[resort.__platform_rating] = true;

            return agg;
          }, {});

          const { currency } = context.state;
          const rangeInSelectedCurrency =
            currencyRanges[currency.code].resort.filterRange;
          const rate = window.zubluCurrencyTable[currency.code];

          const visibleBudgets = countBy(results.hits, (hit) => {
            if (hit.__odyssey_price <= rangeInSelectedCurrency[0][1] * rate) {
              return "min0";
            } else if (
              hit.__odyssey_price <=
              rangeInSelectedCurrency[50][1] * rate
            ) {
              return "min50";
            } else if (
              hit.__odyssey_price <=
              rangeInSelectedCurrency[100][1] * rate
            ) {
              return "min100";
            } else if (
              hit.__odyssey_price <=
              rangeInSelectedCurrency[250][1] * rate
            ) {
              return "min250";
            }

            return "min500";
          });

          context.commit("setStatistics", {
            visibleDestinations,
            visibleRatings,
            visibleBudgets,
          });
        });
    },
  },
  plugins: [plugin],
});

store.watch(
  (state, getters) => {
    return getters.sortedResults;
  },
  (sortedResults) => {
    const currentPage = store.state.page;
    const perPage = 25;
    const maxPages = Math.ceil(sortedResults.length / perPage);

    if (currentPage > maxPages) {
      store.commit("setPage", 1);
    }
  }
);

window.fetchReviewsByIds = once((body) => {
  return axios.post(
    `${window.ZUBLU_VARIABLES.ODYSSEY_API}/api/v1/reviews/queries`,
    body
  );
});

const apps = document.querySelectorAll(".vue-app");

Array.prototype.forEach.call(
  apps,
  (el) =>
    new Vue({
      el,
      template: el.innerHTML,
      store,
    })
);
